import React, { useEffect } from 'react';
import { Permissions, useAuth } from '@kp/react-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBreadcrumb } from '../../../contexts/breadcrumb-context';
import { entityBreadcrumbLabel } from '../../../utils/breadcrumb';
import { useAttributeListQuery } from '../../../__generated__/types';
import * as Entities from '../../../constants/Entities';
import { AttributeList } from './AttributeList';
import { ErrorMessage } from '../../../components/Errors';
import { entityLocation } from '../../../utils/entity';
import { useFilters } from '../../../hooks/filters';
import { useHeader } from '../../../contexts/header-context';

export const AttributeListContainer: React.FC = () => {
  const { t } = useTranslation(['attributes', 'general']);
  const navigate = useNavigate();
  const { page, pageSize, query, setPage, setPageSize, setQuery } =
    useFilters();
  const { setTitle } = useHeader();
  const { hasPermission } = useAuth();

  const handleAdd = () => navigate(entityLocation(Entities.ATTRIBUTE, `new`));

  const { loading, error, data } = useAttributeListQuery({
    variables: {
      take: pageSize,
      skip: page * pageSize,
      where: {
        name: { contains: query },
      },
    },
  });

  useBreadcrumb({
    title: entityBreadcrumbLabel(Entities.ATTRIBUTE, t),
    location: Entities.ATTRIBUTE.path,
  });

  const attributes = data?.attributes?.items ?? [];
  const count = data?.attributes?.totalCount ?? 0;

  useEffect(() => {
    setTitle({ main: t('attributes:list.title') });
  }, [setTitle, t]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <AttributeList
      loading={loading}
      page={page}
      pageSize={pageSize}
      count={count}
      onChangePageSize={setPageSize}
      onChangePage={setPage}
      searchValue={query}
      onSearchSubmit={setQuery}
      attributes={attributes}
      onAdd={hasPermission(Permissions.AttributesWrite) ? handleAdd : undefined}
    />
  );
};
